
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ArrowLeft, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase/client';
import { Company, CompanyDomain } from '@/types/company';
import { Skeleton } from '@/components/ui/skeleton';
import { DomainTable } from '@/components/settings/domain/DomainTable';
import { AddDomainDialog } from './dialogs/AddDomainDialog';
import { DeleteDomainDialog } from '@/components/settings/domain/DeleteDomainDialog';

export function CompanyDomains() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company | null>(null);
  const [domains, setDomains] = useState<CompanyDomain[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<CompanyDomain | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cargar empresa y dominios desde Supabase
  const fetchCompanyAndDomains = async () => {
    if (!companyId) return;
    
    setIsLoading(true);
    try {
      // Obtener empresa
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();
      
      if (companyError) throw companyError;
      
      // Obtener dominios
      const { data: domainsData, error: domainsError } = await supabase
        .from('company_domains')
        .select('*')
        .eq('company_id', companyId);
      
      if (domainsError) throw domainsError;
      
      // Transformar los datos al formato de Company y CompanyDomain
      const formattedCompany: Company = {
        id: companyData.id,
        name: companyData.name,
        description: companyData.description || '',
        status: companyData.status,
        contactEmail: companyData.contact_email,
        contactPerson: companyData.contact_person,
        createdAt: new Date(companyData.created_at),
        updatedAt: new Date(companyData.updated_at)
      };
      
      const formattedDomains: CompanyDomain[] = domainsData.map(d => ({
        id: d.id,
        domain: d.domain,
        companyId: d.company_id,
        isPrimary: d.is_primary,
        isActive: d.is_active,
        createdAt: new Date(d.created_at)
      }));
      
      setCompany(formattedCompany);
      setDomains(formattedDomains);
    } catch (error) {
      console.error('Error al cargar empresa y dominios:', error);
      toast.error('Error al cargar la empresa y sus dominios');
      navigate('/admin/companies');
    } finally {
      setIsLoading(false);
    }
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    fetchCompanyAndDomains();
  }, [companyId]);

  // Agregar dominio
  const handleAddDomain = async (domain: string) => {
    if (!companyId || !domain) return;
    
    setIsSubmitting(true);
    try {
      // Verificar si el dominio ya existe para esta empresa
      const { data: existingDomains, error: checkError } = await supabase
        .from('company_domains')
        .select('domain')
        .eq('company_id', companyId)
        .eq('domain', domain);
      
      if (checkError) throw checkError;
      
      if (existingDomains && existingDomains.length > 0) {
        toast.error('Este dominio ya ha sido agregado para esta empresa');
        return;
      }
      
      // Determinar si este dominio será primario
      const isPrimary = domains.length === 0;
      
      // Insertar nuevo dominio
      const { data, error } = await supabase
        .from('company_domains')
        .insert({
          company_id: companyId,
          domain: domain,
          is_primary: isPrimary,
          is_active: true
        })
        .select();
      
      if (error) throw error;
      
      // Agregar el nuevo dominio al estado
      if (data && data.length > 0) {
        const newDomain: CompanyDomain = {
          id: data[0].id,
          domain: data[0].domain,
          companyId: data[0].company_id,
          isPrimary: data[0].is_primary,
          isActive: data[0].is_active,
          createdAt: new Date(data[0].created_at)
        };
        
        setDomains([...domains, newDomain]);
        setIsAddDialogOpen(false);
        toast.success(`Dominio ${domain} agregado correctamente`);
      }
    } catch (error) {
      console.error('Error al agregar dominio:', error);
      toast.error('Error al agregar el dominio');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Eliminar dominio
  const handleDeleteDomain = (domain: CompanyDomain) => {
    setSelectedDomain(domain);
    setIsDeleteDialogOpen(true);
  };

  // Confirmar eliminación de dominio
  const confirmDeleteDomain = async () => {
    if (!selectedDomain) return;
    
    setIsSubmitting(true);
    try {
      // No permitir eliminar dominios primarios
      if (selectedDomain.isPrimary) {
        toast.error('No se puede eliminar el dominio principal. Establece otro dominio como principal primero.');
        return;
      }
      
      // Eliminar dominio
      const { error } = await supabase
        .from('company_domains')
        .delete()
        .eq('id', selectedDomain.id);
      
      if (error) throw error;
      
      // Actualizar estado
      setDomains(domains.filter(d => d.id !== selectedDomain.id));
      setIsDeleteDialogOpen(false);
      setSelectedDomain(null);
      toast.success(`Dominio eliminado correctamente`);
    } catch (error) {
      console.error('Error al eliminar dominio:', error);
      toast.error('Error al eliminar el dominio');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Establecer dominio como primario
  const handleSetPrimary = async (domainId: string) => {
    setIsSubmitting(true);
    try {
      // Primero, quitar el estado primario de cualquier dominio actual
      const { error: updateError1 } = await supabase
        .from('company_domains')
        .update({ is_primary: false })
        .eq('company_id', companyId)
        .eq('is_primary', true);
      
      if (updateError1) throw updateError1;
      
      // Luego, establecer el nuevo dominio como primario
      const { error: updateError2 } = await supabase
        .from('company_domains')
        .update({ is_primary: true })
        .eq('id', domainId);
      
      if (updateError2) throw updateError2;
      
      // Actualizar estado
      const updatedDomains = domains.map(d => ({
        ...d,
        isPrimary: d.id === domainId
      }));
      
      setDomains(updatedDomains);
      const domain = domains.find(d => d.id === domainId);
      toast.success(`Dominio ${domain?.domain} establecido como primario`);
    } catch (error) {
      console.error('Error al establecer dominio primario:', error);
      toast.error('Error al establecer el dominio primario');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/companies/${companyId}`)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Skeleton className="h-8 w-64" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-1/3" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-40 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/companies')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">Empresa no encontrada</h1>
        </div>
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground">La empresa no pudo ser encontrada. Verifique el ID e intente de nuevo.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/companies/${companyId}`)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">Dominios de {company.name}</h1>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Agregar Dominio
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dominios</CardTitle>
          <CardDescription>
            Administra los dominios asociados a esta empresa
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DomainTable 
            domains={domains} 
            onDeleteDomain={handleDeleteDomain}
            onSetPrimary={handleSetPrimary}
          />
          
          {domains.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <p>No hay dominios registrados para esta empresa.</p>
              <p>Agrega un dominio para que los usuarios puedan registrarse con correos de este dominio.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Diálogos */}
      <AddDomainDialog 
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onSubmit={handleAddDomain}
        isSubmitting={isSubmitting}
      />
      
      <DeleteDomainDialog
        domain={selectedDomain}
        isOpen={isDeleteDialogOpen}
        isSubmitting={isSubmitting}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirmDelete={confirmDeleteDomain}
      />
    </div>
  );
}
